import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { CheckCircle, Trash2, Pencil } from 'lucide-react';
import { Table, Modal, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@rickylandino/react-messages';
import Globals from '../../../config/globals';
import { useUnprintedRecallLettersCount, useUnprintedRecallLettersData, useDismissUnprintedRecallLetter } from '../../../hooks/useWidgetData';
import { useSelectedDashboardFacility } from '../../../hooks/useSelectedDashboardFacility';
import { getPatientInformationByIDNew } from '../../../common/services/ApiService';
import { renderRecallLetter, getAllRecallTypes } from '../../../common/services/next-gen/WidgetDataService';
import { extractApiErrorDetail } from '../../../common/services/apiError';
import PatientDemographicsSlideout from '../../Patient/PatientDemographicsSlideout';
import WidgetCountState from '../WidgetCountState';
import WidgetDataError from '../WidgetDataError';

// The three buckets the server derives from RecallDate. Kept in step with the PrintStatus
// constants in WidgetDataService - the filter posts these strings straight back.
const PRINT_STATUS_OPTIONS = [
    { value: '30 Days Prior', label: '30 Days Prior' },
    { value: '30 Days Past', label: '30 Days Past' },
    { value: '1 Year Past', label: '1 Year Past' }
];

// The widget's query excludes Screening Ultrasound outright, so it is never a useful filter option.
const EXCLUDED_RECALL_TYPES = ['Screening Ultrasound'];

function UnprintedRecallLettersWidget({ isCollapsed = false, isExpanded = false, setIsLoading, setSharedState, queryEnabled = true, history }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [openingChartId, setOpeningChartId] = useState(null);
    const [printingId, setPrintingId] = useState(null);
    const [showDismissModal, setShowDismissModal] = useState(false);
    const [dismissTarget, setDismissTarget] = useState(null);
    const [editDemographicsTarget, setEditDemographicsTarget] = useState(null);
    const [selectedRecallTypes, setSelectedRecallTypes] = useState([]);
    const [selectedPrintStatuses, setSelectedPrintStatuses] = useState([]);

    const { selectedFacilities } = useSelectedDashboardFacility();
    const shouldLoadFullData = !isCollapsed || isExpanded;

    const { data: allRecallTypes = [] } = useQuery({
        queryKey: ['recallTypes'],
        queryFn: getAllRecallTypes,
        staleTime: 10 * 60 * 1000
    });

    const recallTypeOptions = useMemo(
        () => (allRecallTypes || [])
            .filter(rt => !EXCLUDED_RECALL_TYPES.includes(rt.recallTypeDescription))
            .map(rt => ({ value: rt.recallTypeDescription, label: rt.recallTypeDescription })),
        [allRecallTypes]
    );

    const hasActiveFilters = selectedRecallTypes.length > 0 || selectedPrintStatuses.length > 0;

    const { data: countData, isLoading: isLoadingCount, isError: isCountError, refetch: refetchCount } = useUnprintedRecallLettersCount((isCollapsed && !isExpanded) && queryEnabled, selectedFacilities);

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useUnprintedRecallLettersData(currentPage, pageSize, shouldLoadFullData && queryEnabled, selectedFacilities, selectedRecallTypes, selectedPrintStatuses);

    const { mutate: dismissRecall, isPending: isDismissing } = useDismissUnprintedRecallLetter();

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    useEffect(() => {
        if (setIsLoading && isExpanded) {
            setIsLoading(isLoading || isFetching);
        }
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    useEffect(() => {
        if (shouldLoadFullData && isError) {
            console.error('Failed to load unprinted recall letters:', error);
        }
    }, [shouldLoadFullData, isError, error]);

    useEffect(() => {
        if (setSharedState && refetch) {
            setSharedState(prev => ({ ...prev, refetch }));
        }
    }, [setSharedState, refetch]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric'
        });
    }, []);

    const formatMRN = useCallback((mrn) => {
        if (!mrn) return '';
        return mrn.replace(/(.{10})/g, '$1 ').trim();
    }, []);

    const getPrintStatusColor = useCallback((status) => {
        if (!status) return undefined;
        if (status === '30 Days Prior') return Globals.theme === 'dark' ? '#86efac' : '#16a34a';
        if (status === '30 Days Past') return Globals.theme === 'dark' ? '#fcd34d' : '#d97706';
        return Globals.theme === 'dark' ? '#f87171' : '#dc2626';
    }, []);

    const handleOpenChart = useCallback(async (record) => {
        if (!record.patientId) return;
        setOpeningChartId(record.patientRecallId);
        try {
            const data = await getPatientInformationByIDNew(record.patientId);
            history.push({
                pathname: '/patient-dashboard',
                state: { selectedObject: data.patientInformation, examInfo: data.examInfo, patientEthnicity: data.patientEthnicity }
            });
        } catch {
            toast.error('Failed to open patient chart.');
        } finally {
            setOpeningChartId(null);
        }
    }, [history]);

    const handlePrintLetter = useCallback(async (record) => {
        if (!record.patientRecallId) return;
        setPrintingId(record.patientRecallId);
        try {
            const blob = await renderRecallLetter(record.patientRecallId);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 100);
            dismissRecall(record.patientRecallId);
        } catch (err) {
            // Surface the API's own explanation - these are usually configuration problems
            // ("no recall print type configured...") that retrying will never resolve.
            const detail = await extractApiErrorDetail(err, 'Please try again.');
            toast.error(`Failed to generate recall letter. ${detail}`);
        } finally {
            setPrintingId(null);
        }
    }, [dismissRecall]);

    const handleDismiss = useCallback((record) => {
        setDismissTarget(record);
        setShowDismissModal(true);
    }, []);

    const handleConfirmDismiss = useCallback(() => {
        if (!dismissTarget?.patientRecallId) return;
        dismissRecall(dismissTarget.patientRecallId, {
            onSuccess: () => {
                setShowDismissModal(false);
                setDismissTarget(null);
            },
            onError: () => {
                toast.error('Failed to remove from widget. Please try again.');
            }
        });
    }, [dismissTarget, dismissRecall]);

    const accentColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    const mutedColor = Globals.theme === 'dark' ? '#a1a1aa' : '#71717a';

    const columns = useMemo(() => [
        {
            title: 'Patient Name',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 180,
        },
        {
            title: 'MRN',
            dataIndex: 'mrn',
            key: 'mrn',
            render: (mrn) => <span style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{formatMRN(mrn)}</span>,
            width: 140,
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 150,
        },
        {
            title: 'DOB',
            dataIndex: 'dob',
            key: 'dob',
            render: (dob) => formatDate(dob),
            width: 110,
        },
        {
            title: 'Cell Phone',
            dataIndex: 'cellPhone',
            key: 'cellPhone',
            width: 130,
        },
        {
            title: 'Most Recent Exam',
            dataIndex: 'mostRecentExamDate',
            key: 'mostRecentExamDate',
            render: (d) => formatDate(d),
            width: 140,
        },
        {
            title: 'Recall Type',
            dataIndex: 'recallType',
            key: 'recallType',
            width: 160,
        },
        {
            title: 'Recall Date',
            dataIndex: 'recallDate',
            key: 'recallDate',
            render: (d) => formatDate(d),
            width: 110,
        },
        {
            title: 'Print Status',
            dataIndex: 'printStatus',
            key: 'printStatus',
            width: 150,
            render: (status) => (
                <span style={{ color: getPrintStatusColor(status), fontWeight: 500 }}>{status}</span>
            ),
        },
        {
            title: 'Failure Reason',
            dataIndex: 'failureReason',
            key: 'failureReason',
            width: 200,
            render: (reason) => reason
                ? <span style={{ color: Globals.theme === 'dark' ? '#f87171' : '#dc2626', fontSize: '0.875rem' }}>{reason}</span>
                : <span style={{ color: Globals.theme === 'dark' ? '#71717a' : '#a1a1aa', fontStyle: 'italic' }}>—</span>,
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 265,
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={() => handleOpenChart(record)}
                        disabled={openingChartId === record.patientRecallId}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#ec4899' : '#db2777',
                            color: 'white',
                            border: 'none',
                            cursor: openingChartId === record.patientRecallId ? 'default' : 'pointer',
                            opacity: openingChartId === record.patientRecallId ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (openingChartId !== record.patientRecallId)
                                e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#db2777' : '#be185d';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
                        }}
                    >
                        {openingChartId === record.patientRecallId ? 'Opening...' : 'Open Chart'}
                    </button>
                    <button
                        onClick={() => handlePrintLetter(record)}
                        disabled={printingId === record.patientRecallId}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                        style={{
                            backgroundColor: Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5',
                            color: Globals.theme === 'dark' ? '#e4e4e7' : '#3f3f46',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d4d4d8'}`,
                            cursor: printingId === record.patientRecallId ? 'default' : 'pointer',
                            opacity: printingId === record.patientRecallId ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (printingId !== record.patientRecallId)
                                e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#52525b' : '#e4e4e7';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = Globals.theme === 'dark' ? '#3f3f46' : '#f4f4f5';
                        }}
                    >
                        {printingId === record.patientRecallId ? 'Printing...' : 'Print Letter'}
                    </button>
                    <button
                        onClick={() => setEditDemographicsTarget(record)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d4d4d8'}`,
                            color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = accentColor;
                            e.currentTarget.style.borderColor = accentColor;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = Globals.theme === 'dark' ? '#a1a1aa' : '#71717a';
                            e.currentTarget.style.borderColor = Globals.theme === 'dark' ? '#52525b' : '#d4d4d8';
                        }}
                        title="Edit Demographics"
                    >
                        <Pencil size={14} />
                    </button>
                    <button
                        onClick={() => handleDismiss(record)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d4d4d8'}`,
                            color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#ef4444';
                            e.currentTarget.style.borderColor = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = Globals.theme === 'dark' ? '#a1a1aa' : '#71717a';
                            e.currentTarget.style.borderColor = Globals.theme === 'dark' ? '#52525b' : '#d4d4d8';
                        }}
                        title="Remove from widget"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            ),
        },
    ], [formatDate, formatMRN, getPrintStatusColor, handleOpenChart, handlePrintLetter, handleDismiss, openingChartId, printingId]);

    const getCountColor = () => Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    const getCountMessage = () => count === 1 ? 'unprinted recall letter' : 'unprinted recall letters';

    if (isCollapsed && !isExpanded) {
        return (
            <WidgetCountState
                isLoading={isLoadingCount}
                isError={isCountError}
                hasData={countData !== undefined}
                onRetry={refetchCount}
                mutedColor={mutedColor}
            >
                <div className="text-5xl font-light mb-2" style={{ color: getCountColor() }}>
                    {count.toLocaleString()}
                </div>
                <div className="text-sm" style={{ color: mutedColor }}>
                    {getCountMessage()}
                </div>
            </WidgetCountState>
        );
    }

    return (
        <div className="space-y-4">
            {/* Recall type and print status filters — always visible when expanded */}
            <div className="flex flex-wrap gap-2">
                <Select
                    mode="multiple"
                    allowClear
                    // One tag plus a "+N" overflow, with the tag text itself ellipsised, keeps the
                    // control a single fixed-height row however many options are selected.
                    // (antd 4.15 predates maxTagCount="responsive".)
                    maxTagCount={1}
                    maxTagTextLength={16}
                    style={{ width: 260 }}
                    placeholder="Filter by recall type..."
                    value={selectedRecallTypes}
                    onChange={(vals) => { setSelectedRecallTypes(vals); setCurrentPage(1); }}
                    options={recallTypeOptions}
                />
                <Select
                    mode="multiple"
                    allowClear
                    maxTagCount={1}
                    maxTagTextLength={16}
                    style={{ width: 260 }}
                    placeholder="Filter by print status..."
                    value={selectedPrintStatuses}
                    onChange={(vals) => { setSelectedPrintStatuses(vals); setCurrentPage(1); }}
                    options={PRINT_STATUS_OPTIONS}
                />
            </div>
            {isError ? (
                <WidgetDataError onRetry={refetch} mutedColor={mutedColor} />
            ) : !isLoading && count === 0 ? (
                <div className="flex flex-col items-center justify-center py-4">
                    <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: getCountColor() }} />
                    <p style={{ color: getCountColor() }}>
                        {hasActiveFilters
                            ? 'No Unprinted Recall Letters Match The Selected Filters'
                            : 'No Unprinted Recall Letters Found'}
                    </p>
                </div>
            ) : (
                <>
                    <Table
                        columns={columns}
                        dataSource={results}
                        rowKey={(record) => record.patientRecallId}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: totalCount,
                            onChange: (page, newPageSize) => {
                                setCurrentPage(page);
                                if (newPageSize !== pageSize) {
                                    setPageSize(newPageSize);
                                    setCurrentPage(1);
                                }
                            },
                            pageSizeOptions: ['25', '50', '100', '200'],
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} recall letters`,
                        }}
                        size="small"
                        scroll={{ y: 500 }}
                        virtual
                        loading={(isLoading && !isExpanded) || isFetching}
                    />

                    <Modal
                        visible={showDismissModal}
                        title="Remove from Widget"
                        onCancel={() => { setShowDismissModal(false); setDismissTarget(null); }}
                        footer={[
                            <button
                                key="cancel"
                                onClick={() => { setShowDismissModal(false); setDismissTarget(null); }}
                                className="px-4 py-1.5 rounded-lg font-medium text-sm"
                                style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d1d5db'}`,
                                    cursor: 'pointer',
                                    marginRight: 8
                                }}
                            >
                                Cancel
                            </button>,
                            <button
                                key="confirm"
                                onClick={handleConfirmDismiss}
                                disabled={isDismissing}
                                className="px-4 py-1.5 rounded-lg font-medium text-sm"
                                style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', cursor: 'pointer' }}
                            >
                                Remove
                            </button>
                        ]}
                    >
                        <p>
                            Removing <strong>{dismissTarget?.patientName}</strong> will hide this recall from the widget.
                        </p>
                        <p>Are you sure?</p>
                    </Modal>
                </>
            )}

            <PatientDemographicsSlideout
                patientId={editDemographicsTarget?.patientId}
                visible={!!editDemographicsTarget}
                onClose={() => setEditDemographicsTarget(null)}
                onSaved={(printAfterSave) => {
                    if (printAfterSave && editDemographicsTarget) {
                        handlePrintLetter(editDemographicsTarget);
                    }
                }}
            />
        </div>
    );
}

export default withRouter(UnprintedRecallLettersWidget);
